import React from 'react';
import styled from 'styled-components';
import TeamsOnboardingSlackInvite from './TeamsOnboardingSlackInvite';
import TeamsOnboardingSurvey from './TeamsOnboardingSurvey';
import {
  ONBOARDING_ACTION,
  OnboardingContext,
} from '../../../contexts/onboarding';
import { CloseIcon } from '../../Icons';
import { toast } from 'react-toastify';
import {
  joinEnkiCommunity,
  updateOnboardingAnswer,
  updateOnboardingSlackJoin,
} from '../../../hydra';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { P } from '../../elements/Text';
import { H3 } from '../../elements/Text';
import Button from '../../elements/Button';

const ContentWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 1;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const ErrorContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  max-width: ${({ theme }) => theme.dimensions.readableColumnWidth};

  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: -82px;
`;

export default function TrackOnboardingContent({
  onRefreshData,
  isRefreshing,
  onClose,
}: {
  onRefreshData: () => Promise<void>;
  isRefreshing: boolean;
  onClose: () => void;
}) {
  const [onboardingState, dispatch] = React.useContext(OnboardingContext);
  const { getTokenSilently } = useAuth();

  const onCloseHandler = async () => {
    if (!isCompleted) {
      if (window.confirm('Are you sure you want to dismiss your onboarding?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const trackId = parseInt(onboardingState.track.trackId);

  const submitSurveyStepMutation = useMutation({
    mutationFn: async (stepIndex: number) => {
      const token = await getTokenSilently();

      await updateOnboardingAnswer(token, {
        trackId,
        surveyStep: {
          stepIndex,
          answers: onboardingState.track.survey[stepIndex].questions,
        },
      });

      dispatch({
        type: ONBOARDING_ACTION.TRACK.SUBMIT_SURVEY_STEP,
        payload: {
          stepIndex,
        },
      });
    },
    onError: () => {
      toast.error(
        'Sorry, something went wrong. Please get in contact with our team.'
      );
    },
  });

  const submitOnboardingMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      await updateOnboardingSlackJoin(token, {
        trackId,
        didJoinSlack: true,
      });
    },
    onSuccess: async () => {
      await onRefreshData();
    },
    onError: () => {
      toast.error(
        'Sorry, something went wrong. Please get in contact with our team.'
      );
    },
  });

  const joinSlackMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      await joinEnkiCommunity(token, {
        source: 'track-onboarding',
      });
      dispatch({
        type: ONBOARDING_ACTION.TRACK.CONFIRM_JOIN_SLACK,
      });
    },
    onSuccess: () => {
      submitOnboardingMutation.mutate();
    },
    onError: () => {
      toast.error(
        'Sorry, something went wrong. Please get in contact with our team.'
      );
    },
  });

  const onFinish = async () => {
    await onRefreshData();
    toast.success(
      "Onboarding complete! We'll be in touch with the kick-off call info."
    );
    onClose();
  };

  const currentStep = (() => {
    const stepIndex = [
      ...onboardingState.track.survey.map((step) => step.isCompleted),
      onboardingState.track.slack.isCompleted,
    ].findIndex((x) => !x);
    return stepIndex === -1 ? onboardingState.track.survey.length : stepIndex;
  })();

  const screens = [
    ...onboardingState.track.survey.map((step, index) => ({
      component: (
        <TeamsOnboardingSurvey
          stepIndex={index}
          submitAnswers={() => submitSurveyStepMutation.mutate(index)}
          isLoading={
            submitSurveyStepMutation.isLoading ||
            submitOnboardingMutation.isLoading
          }
        />
      ),
      name: step.name,
    })),
    {
      component: (
        <TeamsOnboardingSlackInvite
          joinSlack={() => joinSlackMutation.mutate()}
          isInvitingToSlack={
            joinSlackMutation.isLoading || joinSlackMutation.isIdle
          }
          isRefreshing={isRefreshing}
          onFinish={onFinish}
        />
      ),
      name: 'Connect to Slack',
    },
  ];

  const isCompleted =
    onboardingState.track.survey.every((step) => step.isCompleted) &&
    onboardingState.track.slack.isCompleted;

  if (
    submitSurveyStepMutation.isError ||
    joinSlackMutation.isError ||
    submitOnboardingMutation.isError
  ) {
    return (
      <ContentWrapper>
        <ErrorContentWrapper>
          <H3>Something went wrong.</H3>
          <P>Please reach out to us to get this fixed as soon as possible</P>
          <Button
            style={{ width: 'fit-content' }}
            label="Get in touch"
            onClick={() => window.open('mailto:concierge@enki.com')}
            faIcon={'fas fa-envelope'}
          />
        </ErrorContentWrapper>
      </ContentWrapper>
    );
  }

  return (
    <>
      <Column>
        <CloseIcon
          onClick={onCloseHandler}
          style={{
            padding: 0,
            fontSize: '36px',
            position: 'absolute',
            right: 23,
            top: 23,
          }}
        />
      </Column>

      <ContentWrapper>
        {currentStep !== -1 && screens[currentStep].component}
      </ContentWrapper>
    </>
  );
}
